import {createStore} from '@stripe-internal/state-manager';
import type {
  Screen,
  FeatureFlags,
  SupportedCurrency,
  WalletOption,
  WalletSession,
  PaymentDetails,
  TransactionDetails,
  ExternalPaymentDetails,
  WalletConnection,
  SettlementPartner,
  State,
  NetworkType,
  MerchantBranding,
  MandateDetails,
  Overlay,
  ErrorMessage,
  ExchangeOption,
  DirectDeposit,
  AnyCryptoDeposit,
} from 'src/v2/state/types';
import {COLORS} from 'src/v2/util/color';

export type SessionState = {
  // Session model
  id: string;
  livemode: boolean;
  merchant: string;
  consumerAccount: string | null;
  state: State;
  settlementPartner: SettlementPartner;
  depositAddresses: Record<string, string>;
  directDepositAddresses: Record<string, string>;
  spenderContractAddresses: Record<string, string>;
  clientSecret: string;
  setupFutureUsage: boolean;
  featureFlags: FeatureFlags;
  supportedCurrencies: SupportedCurrency[];
  walletSessions: WalletSession[];
  consumerWallet: string | null;
  disabledWalletProviders: string[];
  validBefore: string;

  // only for payin session
  paymentDetails: PaymentDetails;
  transactionDetails: TransactionDetails;
  externalPaymentDetails: ExternalPaymentDetails | null;
  hasSucceededOrProcessingSetupAttempt: boolean;

  // only for setup session
  mandateDetails: MandateDetails;

  // Other state
  screen: Screen;
  // For the most recent wallet that has attempted to connect or has connected in this session.
  // Note this doesn't always match the active wallet.
  newWallet: {
    walletOption: WalletOption;
    networkType: NetworkType | null;
  } | null;
  exchangeOption: ExchangeOption | null;
  walletConnection: WalletConnection;
  merchantBranding: MerchantBranding;
  overlay: Overlay | null;
  errorBanner: ErrorMessage | null;
  directDeposit: DirectDeposit;
  anyCryptoDeposit: AnyCryptoDeposit;
  selectedCurrency: SupportedCurrency | null;
};

export type Store = ReturnType<typeof createStore<SessionState>>;

const initialValues: SessionState = {
  // Session model
  id: '',
  livemode: false,
  merchant: '',
  consumerAccount: null,
  state: 'checkout',
  disabledWalletProviders: [],
  settlementPartner: 'paxos',
  depositAddresses: {},
  directDepositAddresses: {},
  spenderContractAddresses: {},
  clientSecret: '',
  setupFutureUsage: false,
  featureFlags: {
    useAsyncConfirmationFlow: false,
    enable_crypto_payins_pay_from_exchange: false,
    enable_crypto_payins_direct_deposit: false,
    enable_underpayment_detection: false,
    enable_gasless_v2: false,
    enable_smart_contract_wallet_detection: false,
    crypto_payins_generic_payment_title: false,
    enable_pay_with_any_crypto: false,
  },
  supportedCurrencies: [],
  walletSessions: [],
  consumerWallet: null,
  validBefore: '',
  paymentDetails: {
    amount: 0,
    currency: '',
    payauthzId: '',
    returnUrl: '',
    originalAmount: null,
    originalCurrency: null,
  },
  transactionDetails: {
    walletAddress: null,
    blockchainTxId: null,
    destinationAddress: null,
    destinationCurrency: null,
    destinationNetwork: null,
    destinationAmount: null,
    solanaLatestBlockhash: null,
    solanaDestinationTokenAccountAddress: null,
  },
  externalPaymentDetails: null,
  hasSucceededOrProcessingSetupAttempt: false,
  mandateDetails: {
    fpmandateId: '',
    returnUrl: '',
  },
  merchantBranding: {
    backgroundColor: COLORS.defaultBackgroundColor,
    buttonColor: COLORS.defaultButtonColor,
    businessName: null,
    logoUrl: null,
  },

  // Other state
  screen: 'WALLET_SELECT',
  newWallet: null,
  exchangeOption: null,
  walletConnection: {
    ready: false,
    activeWallet: null,
    connectedWallets: [],
    walletKey: 'metamask',
    walletConnectQrCodeUri: null,
    device: 'desktop',
  },
  overlay: null,
  errorBanner: null,
  directDeposit: {
    selectedDirectDepositNetwork: null,
    selectedDirectDepositCurrency: null,
    refundMethod: null,
    customRefundAddress: '',
    isRefundAddressApplied: false,
    customerEmail: '',
    underpaymentTransferCount: 0,
  },
  anyCryptoDeposit: {
    step: 'deposit',
    maxRelayRequestStatus: null,
    selectedToken: null,
    selectedNetwork: null,
    refundMethod: 'sendToPaymentAddress',
    customRefundAddress: '',
    customerEmail: '',
    quote: {
      depositAddress: null,
      inputAmount: null,
      inputChain: null,
      inputCurrency: null,
    },
  },
  selectedCurrency: null,
};

const store = createStore(initialValues);

export const getStore = () => store;

export const {setValue, useSelector} = getStore()!;
